import { useState } from 'react';
import compact from 'lodash/compact';
import uniqBy from 'lodash/uniqBy';
import { useQuery } from '@tanstack/react-query';

import { FrontendTaskStatus, MedicalCaseData, type Nullable } from 'chaika-frontend-types/frontend-cms';

import { isDefined } from '@utils/isDefined';
import { formatFullName } from '@utils/formatFullName';
import { DateFormat, DateHelper } from '@utils/DateHelper';
import { store } from '@store';
import { OptionType } from '@components/Select';
import { medicalCaseQueryOptions } from '@store/MedicalCase/queries/medicalCaseQueries';
import { ehrQueryOptions } from '@patient/queries/ehrQueries';
import { useOpenedEhrId } from '@patient/hooks/useOpenedEhrId';

import type { TaskFieldData } from '@common/Task';
import { useTaskForm } from '@common/Task/TaskRightMenu/context/TaskFormContext';

interface Params {
    isPatientOptional: boolean;
    canEditMedicalCase?: boolean;
}

interface Data {
    canManagePatient: boolean;
    isPatientSectionVisible: boolean;
    isPatientEditable: boolean;
    isMedicalCaseEditable: boolean;
    isPatientSelectDisabled: boolean;
    medicalCaseIds: string[];
    patientData: TaskFieldData;
    phone?: string;
    mergedEhrIds?: string[];
    entryId?: string;
    medicalCaseValues: { id: string; value: string; onClick(): void }[];
    medicalCases: MedicalCaseData[];
    onPatientSelect(option: OptionType<string>): void;
    onChangeMedicalCaseIds(selectedItem: Nullable<OptionType<string>[]>, name?: string): void;
    onShowPatientSection(): void;
    onRemovePatient(): void;
}

export function useTaskPatientData({ isPatientOptional, canEditMedicalCase = false }: Params): Data {
    const { state, permissions, actions } = useTaskForm();

    const patientId = state.patientId;
    const isCreationMode = state.status === FrontendTaskStatus.Draft;
    const canManagePatient = isPatientOptional && isCreationMode;
    const [isPatientSectionShown, setIsPatientSectionShown] = useState(isDefined(patientId));
    const openedEhrId = useOpenedEhrId();
    const { data: patient } = useQuery(ehrQueryOptions.detail(patientId ?? ''));
    const patientName = isDefined(patient)
        ? compact([
              formatFullName(patient),
              `№${patient.recordId}`,
              DateHelper.formatIfNotNil(patient.birthDate, DateFormat.DateWithDotSeparator),
          ]).join(', ')
        : undefined;

    const mergedEhrIds: string[] = [];
    if (store.ehr.mergedEhrIds.size > 0) {
        mergedEhrIds.push(...store.ehr.mergedEhrIds.values());
    }
    if (patientId !== store.ehr.id) {
        mergedEhrIds.push(store.ehr.id);
    }

    const patientEhrIds = compact([patientId, ...mergedEhrIds]);

    const { data: ehrMedicalCases = [] } = useQuery(
        medicalCaseQueryOptions.list({ ehrIds: patientEhrIds, entryId: state.entryId }),
    );
    const { data: selectedMedicalCases = [] } = useQuery(medicalCaseQueryOptions.byIds(state.medicalCaseIds));

    const medicalCaseValues = selectedMedicalCases.map(medicalCase => ({
        id: medicalCase.id,
        value: medicalCase.name,
        onClick: () => window.open(`/patient/${medicalCase.ehrId}/medical-case/${medicalCase.id}`, '_blank'),
    }));

    const canEdit = state.isEditMode && permissions.canEdit;
    const isPatientSelectDisabled =
        permissions.isPatientSelectDisabled || (state.isEditMode && state.status !== FrontendTaskStatus.Draft);

    const onPatientSelect = ({ value }: OptionType<string>): void => {
        if (value !== patientId) {
            actions.setMedicalCaseIds([]);
        }
        actions.setPatientDataField('patient', value);
    };

    const onChangeMedicalCaseIds = (params: Nullable<OptionType<string>[]>): void => {
        const ids = (params ?? []).map(({ value }) => value);
        actions.setMedicalCaseIds(ids);
    };

    const onShowPatientSection = (): void => {
        setIsPatientSectionShown(true);

        if (!isDefined(patientId) && isDefined(openedEhrId)) {
            actions.setPatientDataField('patient', openedEhrId);
        }
    };

    const onRemovePatient = (): void => {
        setIsPatientSectionShown(false);
        actions.setPatientDataField('patient', undefined);
    };

    return {
        canManagePatient,
        isPatientSectionVisible: canManagePatient ? isPatientSectionShown : isCreationMode || isDefined(patientId),
        isPatientEditable: canEdit && !isPatientSelectDisabled,
        isMedicalCaseEditable: canEdit && (isCreationMode || canEditMedicalCase),
        isPatientSelectDisabled,
        medicalCaseIds: state.medicalCaseIds,
        patientData: [patient?.id, patientName],
        phone: patient?.phone,
        mergedEhrIds: mergedEhrIds.length > 0 ? mergedEhrIds : undefined,
        entryId: state.entryId,
        medicalCaseValues,
        medicalCases: uniqBy([...ehrMedicalCases, ...selectedMedicalCases], 'id'),
        onPatientSelect,
        onChangeMedicalCaseIds,
        onShowPatientSection,
        onRemovePatient,
    };
}
